import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout/DashboardLayout";
import { formatDateTime } from "../../i18n/format";
import { notificationRoute } from "../../lib/notificationTarget";
import {
  getCommonNotificationsListQueryKey,
  useCommonNotificationsList,
  useCommonNotificationsMarkAllReadCreate,
  useCommonNotificationsMarkReadCreate,
} from "../../services/common/generated/common-notifications/common-notifications";
import type {
  CommonNotificationsList200Item,
  CommonNotificationsList200ItemResultsItem,
} from "../../services/common/generated/model";
import "./NotificationsPage.css";

const PAGE_SIZE = 20;

function formatRelative(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return formatDateTime(d);
}

const NotificationsPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation(["b2b", "common"]);
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useCommonNotificationsList({
    page,
    page_size: PAGE_SIZE,
  });

  const markReadMutation = useCommonNotificationsMarkReadCreate({
    mutation: {
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: getCommonNotificationsListQueryKey(),
        }),
    },
  });

  const markAllReadMutation = useCommonNotificationsMarkAllReadCreate({
    mutation: {
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: getCommonNotificationsListQueryKey(),
        }),
    },
  });

  const wrapper = (Array.isArray(data) ? data[0] : data) as CommonNotificationsList200Item | undefined;
  const notifications: CommonNotificationsList200ItemResultsItem[] = wrapper?.results ?? [];
  const total = wrapper?.count ?? notifications.length;
  const hasNext = Boolean(wrapper?.next);
  const hasPrev = Boolean(wrapper?.previous);

  const handleMarkRead = (id?: number) => {
    if (id == null) return;
    markReadMutation.mutate({ id });
  };

  const unreadCount = wrapper?.unreadCount ?? notifications.filter((n) => !n.isRead).length;

  const handleOpen = (n: CommonNotificationsList200ItemResultsItem) => {
    const route = notificationRoute(n, "b2b");
    if (!route) return;
    if (!n.isRead) handleMarkRead(n.id);
    navigate(route.path, { state: route.state });
  };

  return (
    <DashboardLayout>
      <div className="notifications">
        <header className="notifications__header">
          <div>
            <h1 className="notifications__title">{t("notifications.title")}</h1>
            <p className="notifications__subtitle">{t("notifications.summary", { total, unread: unreadCount })}</p>
          </div>
          {unreadCount > 0 ? (
            <button
              type="button"
              className="notifications__mark-read"
              onClick={() => markAllReadMutation.mutate()}
              disabled={markAllReadMutation.isPending}
            >
              {t("notifications.markAllRead")}
            </button>
          ) : null}
        </header>

        {isLoading ? <div className="notifications__loading">{t("notifications.loading")}</div> : null}
        {error ? <div className="notifications__error">{t("notifications.errors.load")}</div> : null}
        {!isLoading && notifications.length === 0 ? (
          <div className="notifications__empty">{t("notifications.empty")}</div>
        ) : null}

        <ul className="notifications__list">
          {notifications.map((n) => (
            <li
              key={n.id ?? n.createdAt}
              className={`notifications__item ${n.isRead ? "notifications__item--read" : ""}`}
            >
              <div className="notifications__item-body">
                <h2 className="notifications__item-title">
                  {notificationRoute(n, "b2b") ? (
                    <button type="button" className="notifications__item-link" onClick={() => handleOpen(n)}>
                      {n.title ?? t("notifications.fallbackTitle")}
                    </button>
                  ) : (
                    (n.title ?? t("notifications.fallbackTitle"))
                  )}
                </h2>
                <p className="notifications__item-message">{n.message}</p>
                <p className="notifications__item-time">{formatRelative(n.createdAt)}</p>
              </div>
              {!n.isRead ? (
                <button
                  type="button"
                  className="notifications__mark-read"
                  onClick={() => handleMarkRead(n.id)}
                  disabled={markReadMutation.isPending && markReadMutation.variables?.id === n.id}
                >
                  {t("notifications.markRead")}
                </button>
              ) : (
                <span className="notifications__chip notifications__chip--muted">{t("notifications.read")}</span>
              )}
            </li>
          ))}
        </ul>

        {(hasPrev || hasNext) && (
          <div className="notifications__pagination">
            <button
              type="button"
              className="notifications__page-btn"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={!hasPrev || isLoading}
            >
              ← {t("common:actions.previous")}
            </button>
            <span className="notifications__page-indicator">{t("notifications.page", { page })}</span>
            <button
              type="button"
              className="notifications__page-btn"
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasNext || isLoading}
            >
              {t("common:actions.next")} →
            </button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default NotificationsPage;
